import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FaEdit, FaTrash, FaCheck, FaTimes, FaUpload, FaIndustry, FaClipboardList } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { publisherAPI, tenderAPI } from '../../../utils/api';
import DataTable from '../../../components_shared/DataTable';
import TableToolbar from '../../../components_shared/TableToolbar';

function TendersTable({ onEdit, onFiles, onCreate, rows: propRows = null, readOnly = false, reloadKey = 0 }) {
  const [data, setData] = useState(propRows || []);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    if (propRows) return;
    setLoading(true);
    try {
      const res = await tenderAPI.getAll();
      setData(Array.isArray(res.data) ? res.data : (res.data.content || []));
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [propRows]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const remove = async (id) => {
    const ok = await Swal.fire({ title: 'Delete Tender?', text: 'This action cannot be undone.', icon: 'warning', showCancelButton: true });
    if (ok.isConfirmed) {
      try {
        await tenderAPI.delete(id);
        await load();
        Swal.fire({ title: 'Deleted', icon: 'success', toast: true, position: 'top-end', timer: 3000 });
      } catch (err) { Swal.fire('Error', err.message, 'error'); }
    }
  };

  const publish = async id => { await tenderAPI.publish(id); await load(); };
  const close = async id => { await tenderAPI.close(id); await load(); };
  const assignPublisher = async tender => {
    const publishersRes = await publisherAPI.getAll();
    const publishers = Array.isArray(publishersRes.data) ? publishersRes.data : publishersRes.data?.content || [];
    const options = Object.fromEntries(publishers.map(p => [p.id, p.name || p.publisher_name || p.username || `Publisher ${p.id}`]));
    const result = await Swal.fire({
      title: 'Assign Publisher',
      input: 'select',
      inputOptions: options,
      inputValue: tender.assigned_publisher_id || '',
      showCancelButton: true,
      confirmButtonText: 'Assign',
      inputPlaceholder: 'Select publisher',
    });
    if (!result.isConfirmed || !result.value) return;
    await tenderAPI.assignPublisher(tender.id, result.value);
    await load();
  };

  const generateWorkOrder = async tender => {
    try {
      await tenderAPI.generateWorkOrder(tender.id);
      await load();
      Swal.fire('Work order generated', 'The assigned publisher can now see the work order.', 'success');
    } catch (err) {
      Swal.fire('Could not generate work order', err?.response?.data?.error || err.message, 'error');
    }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { 
      accessorKey: 'ref_no', 
      header: 'Reference #', 
      cell: ({ getValue }) => <span style={{ fontWeight: 700, color: '#2d3436' }}>{getValue() || '-'}</span> 
    },
    { 
      accessorKey: 'publish_date', 
      header: 'Publish Date', 
      cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleDateString() : '-' 
    },
    { 
      accessorKey: 'closing_date', 
      header: 'Closing Date', 
      cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleDateString() : '-' 
    },
    { 
      accessorKey: 'status', 
      header: 'Status',
      cell: ({ getValue }) => (
        <span className={`eduss-global-status-badge ${getValue()?.toLowerCase()}`}>
          {getValue()}
        </span>
      )
    },
    { accessorKey: 'total_files', header: 'Files', size: 80 },
    {
      id: 'publisher',
      header: 'Publisher',
      cell: ({ row }) => row.original.Publisher?.name || row.original.Publisher?.publisher_name || row.original.assigned_publisher_id || '-',
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          {!readOnly && (
            <>
              <button className="eduss-global-action-btn edit" onClick={() => onEdit(row.original)}><FaEdit /></button>
              <button className="eduss-global-action-btn delete" onClick={() => remove(row.original.id)}><FaTrash /></button>
              {onFiles && <button className="eduss-global-action-btn view" onClick={() => onFiles(row.original)} title="Manage Files"><FaUpload /></button>}
              <button className="eduss-global-action-btn view" onClick={() => assignPublisher(row.original)} title="Assign Publisher"><FaIndustry /></button>
              <button className="eduss-global-action-btn active" onClick={() => generateWorkOrder(row.original)} title="Generate Work Order"><FaClipboardList /></button>
              {row.original.status === 'DRAFT' && (
                <button className="eduss-global-action-btn active" style={{ background: 'rgba(16, 172, 132, 0.1)', color: '#10ac84' }} onClick={() => publish(row.original.id)} title="Publish Tender"><FaCheck /></button>
              )}
              {row.original.status === 'PUBLISHED' && (
                <button className="eduss-global-action-btn delete" onClick={() => close(row.original.id)} title="Close Tender"><FaTimes /></button>
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  const filteredData = data.filter(r => 
    !filter || r.ref_no?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="premium-card" style={{ padding: '0 0 30px 0' }}>
      <TableToolbar 
        title="Procurement Tenders"
        subtitle="Manage public tenders for textbook publication and paper supply"
        onAdd={(!readOnly && onCreate) ? onCreate : null}
        onSearch={setFilter}
      />

      <div style={{ padding: '0 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
          enableSelection={true}
        />
      </div>
    </div>
  );
}

TendersTable.propTypes = { onEdit: PropTypes.func, onFiles: PropTypes.func, onCreate: PropTypes.func, rows: PropTypes.array, readOnly: PropTypes.bool, reloadKey: PropTypes.number };
export default TendersTable;
